import { useState, useEffect } from "react";
import { apiClient } from "../../services/api";
import { useAuth } from "../../hooks/useAuth";
import useHashTab from "../../hooks/useHashTab";
import RefreshButton from "../RefreshButton/RefreshButton";
import "./Settings.css";

const SETTINGS_TABS = ["settings", "users"];

function Settings({ embedded = false }) {
  const { user, isAuthenticated, openDim0, login } = useAuth();
  const [settings, setSettings] = useState({});
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  // When standalone, own the route's hash. When embedded inside Admin (which
  // already owns the outer hash), persist via sessionStorage so subtab choice
  // still survives a refresh without colliding with Admin's tab hash.
  const [activeTab, setActiveTab] = useHashTab(
    SETTINGS_TABS,
    "settings",
    embedded ? { storageKey: "anna_settings_subtab" } : undefined,
  );
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [spotify, setSpotify] = useState({ connected: false, user: "", loading: true });
  const [spotifyConnecting, setSpotifyConnecting] = useState(false);
  const [dim0Opening, setDim0Opening] = useState(false);

  const settingsConfig = [
    {
      key: "openai_agent_api_key",
      label: "OpenAI Agent API Key",
      description: "Used for GPT-5.2 chat (Anna Agent). Track costs separately.",
      placeholder: "sk-...",
      section: "openai",
    },
    {
      key: "groq_api_key",
      label: "Groq API Key",
      description: "Used for fast voice transcription (Whisper via Groq). Get it from console.groq.com.",
      placeholder: "gsk_...",
      section: "voice",
    },
    {
      key: "google_tts_api_key",
      label: "Google Cloud TTS API Key",
      description: "Used for text-to-speech (Anna's voice via Google). Get it from console.cloud.google.com → APIs & Services → Credentials.",
      placeholder: "AIza...",
      section: "voice",
    },
    {
      key: "tts_provider",
      label: "TTS Provider",
      description: "Choose which text-to-speech engine Anna uses.",
      section: "voice",
      type: "select",
      options: [
        { value: "google", label: "Google (es-US-Chirp3-HD-Kore — young feminine)" },
        { value: "f5", label: "F5-TTS (local — fast, free, Spanish)" },
      ],
    },
    {
      key: "notification_email",
      label: "Notification Email",
      description: "Email address where Anna sends reminder notifications.",
      placeholder: "you@example.com",
      section: "notifications",
      type: "email",
    },
    {
      key: "apple_calendar_url",
      label: "Apple Calendar ICS URL",
      description:
        "ICS subscription URL from iCloud Calendar. Go to iCloud.com → Calendar → click the share icon next to your calendar → enable Public Calendar and copy the URL.",
      placeholder: "https://p##-caldav.icloud.com/published/...",
      section: "calendars",
      type: "url",
    },
    {
      key: "spotify_client_id",
      label: "Spotify Client ID",
      description:
        "From developer.spotify.com → Dashboard → Your App → Settings.",
      placeholder: "e.g. 1a2b3c4d5e6f...",
      section: "spotify",
    },
    {
      key: "spotify_client_secret",
      label: "Spotify Client Secret",
      description:
        "From developer.spotify.com → Dashboard → Your App → Settings.",
      placeholder: "e.g. 9z8y7x6w5v...",
      section: "spotify",
    },
    {
      key: "speaker_wake_word",
      label: "Wake Word",
      description: "Built-in: hey_jarvis, alexa, hey_mycroft. Custom ONNX model names also supported.",
      placeholder: "hey_jarvis",
      section: "speaker",
      type: "text",
    },
    {
      key: "speaker_silence_timeout",
      label: "Silence Timeout (seconds)",
      description: "How long to wait for silence before processing speech.",
      placeholder: "2.0",
      section: "speaker",
      type: "text",
    },
    {
      key: "speaker_listen_timeout",
      label: "Listen Timeout (seconds)",
      description: "How long to wait for speech after wake word before giving up. Set lower to avoid long waits.",
      placeholder: "5.0",
      section: "speaker",
      type: "text",
    },
    {
      key: "speaker_min_speech",
      label: "Min Speech Duration (seconds)",
      description: "Minimum speech length to process. Lower to allow short phrases like 'sí' or 'buenos días'.",
      placeholder: "0.3",
      section: "speaker",
      type: "text",
    },
    {
      key: "speaker_followup_timeout",
      label: "Follow-up Timeout (seconds)",
      description: "How long to listen for follow-up after Anna speaks (continuous conversation).",
      placeholder: "5.0",
      section: "speaker",
      type: "text",
    },
    {
      key: "speaker_max_recording",
      label: "Max Recording Duration (seconds)",
      description: "Maximum recording time for both initial and follow-up messages.",
      placeholder: "30",
      section: "speaker",
      type: "text",
    },
    {
      key: "speaker_continuous",
      label: "Continuous Conversation",
      description: "Enable to auto-listen for follow-up questions after Anna speaks.",
      section: "speaker",
      type: "select",
      options: [
        { value: "true", label: "Enabled" },
        { value: "false", label: "Disabled" },
      ],
    },
  ];

  useEffect(() => {
    loadSettings();
    loadSpotifyStatus();
  }, []);

  useEffect(() => {
    if (activeTab === "users") {
      loadUsers();
    }
  }, [activeTab]);

  async function loadUsers() {
    try {
      setUsersLoading(true);
      const data = await apiClient.get("/users");
      setUsers(data.users || []);
    } catch (err) {
      setMessage({ type: "error", text: "Failed to load users" });
    } finally {
      setUsersLoading(false);
    }
  }

  async function handleUserAction(email, action) {
    try {
      await apiClient.put("/users", { email, action });
      setMessage({ type: "success", text: `User ${action}d successfully` });
      await loadUsers();
    } catch (err) {
      setMessage({ type: "error", text: `Failed to ${action} user` });
    }
  }

  async function loadSpotifyStatus() {
    try {
      const data = await apiClient.get("/spotify/status");
      setSpotify({ connected: data.connected, user: data.user || "", product: data.product || "", loading: false });
    } catch {
      setSpotify((prev) => ({ ...prev, loading: false }));
    }
  }

  async function connectSpotify() {
    setSpotifyConnecting(true);
    try {
      const redirectUri = `${window.location.origin}/auth/spotify/callback`;
      const data = await apiClient.get(`/spotify/auth?redirect_uri=${encodeURIComponent(redirectUri)}`);
      if (data.auth_url) {
        window.location.href = data.auth_url;
      }
    } catch (err) {
      setMessage({ type: "error", text: "Failed to start Spotify connection" });
      setSpotifyConnecting(false);
    }
  }

  async function disconnectSpotify() {
    try {
      await apiClient.post("/spotify/disconnect");
      setSpotify({ connected: false, user: "", loading: false });
      setMessage({ type: "success", text: "Spotify disconnected" });
    } catch (err) {
      setMessage({ type: "error", text: "Failed to disconnect Spotify" });
    }
  }

  async function loadSettings() {
    try {
      setLoading(true);
      const data = await apiClient.get("/settings");
      setSettings(data.settings || {});
      // Initialize editing values with current masked values
      const editValues = {};
      for (const config of settingsConfig) {
        const setting = data.settings?.[config.key];
        editValues[config.key] = setting?.value || "";
      }
      setEditing(editValues);
      // Sync TTS provider to localStorage for Chat page
      if (editValues.tts_provider) {
        localStorage.setItem("anna_tts_provider", editValues.tts_provider);
      }
    } catch (err) {
      setMessage({ type: "error", text: "Failed to load settings" });
    } finally {
      setLoading(false);
    }
  }

  function handleChange(key, value) {
    setEditing((prev) => ({ ...prev, [key]: value }));
    // Immediately sync TTS provider to localStorage
    if (key === "tts_provider") {
      localStorage.setItem("anna_tts_provider", value);
    }
  }

  async function handleSave() {
    setSaving(true);
    setMessage(null);
    try {
      const settingsToSave = {};
      for (const [key, value] of Object.entries(editing)) {
        if (value && value.trim()) {
          settingsToSave[key] = value;
        }
      }
      await apiClient.put("/settings", { settings: settingsToSave });
      setMessage({ type: "success", text: "Settings saved successfully" });
      await loadSettings();
    } catch (err) {
      setMessage({ type: "error", text: "Failed to save settings" });
    } finally {
      setSaving(false);
    }
  }

  function handleOpenDim0() {
    setMessage(null);
    try {
      setDim0Opening(true);
      openDim0("/");
      setMessage({ type: "success", text: "Opening Dim0 in a new tab with your current Anna session." });
    } catch (err) {
      setMessage({ type: "error", text: err?.message || "Failed to open Dim0" });
    } finally {
      setDim0Opening(false);
    }
  }

  if (loading) {
    return (
      <div className="settings">
        {!embedded && (
          <div className="settings-header">
            <h2>Settings</h2>
          </div>
        )}
        <div className="settings-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="settings">
      {!embedded && (
        <div className="settings-header">
          <h2>Settings</h2>
          <p className="settings-subtitle">
            Manage API keys, notifications, and users
          </p>
        </div>
      )}

      <div className="settings-tabs" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <button
          className={`settings-tab ${activeTab === "settings" ? "active" : ""}`}
          onClick={() => setActiveTab("settings")}
        >
          ⚙️ Configuration
        </button>
        <button
          className={`settings-tab ${activeTab === "users" ? "active" : ""}`}
          onClick={() => setActiveTab("users")}
        >
          👥 Users
        </button>
        <div style={{ marginLeft: "auto" }}>
          <RefreshButton
            onClick={async () => {
              if (activeTab === "users") {
                await loadUsers();
              } else {
                await loadSettings();
                await loadSpotifyStatus();
              }
            }}
            title={`Refresh ${activeTab}`}
          />
        </div>
      </div>

      {message && (
        <div className={`settings-message ${message.type}`}>
          {message.text}
        </div>
      )}

      {activeTab === "settings" && (
        <>
          <div className="settings-sections">
            <section className="settings-section">
              <h3>Architecture Board</h3>
              <p className="section-description">
                Open the private Dim0 workspace using your current Anna browser session. This does a form POST handoff to the gateway so no token is exposed in the URL.
              </p>
              <div className="dim0-launch-row">
                <div className="dim0-launch-copy">
                  <div className="dim0-launch-label">Dim0 URL</div>
                  <div className="dim0-launch-url">{import.meta.env.VITE_DIM0_URL || "http://192.168.1.218:3000"}</div>
                </div>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={handleOpenDim0}
                  disabled={!isAuthenticated || dim0Opening}
                >
                  {dim0Opening ? "Opening..." : "Open Dim0"}
                </button>
              </div>
            </section>

            <section className="settings-section">
              <h3>OpenAI API Keys</h3>
              <p className="section-description">
                OpenAI API key for the Agent (GPT). Whisper STT is handled by Groq.
              </p>
              {settingsConfig
                .filter((c) => c.section === "openai")
                .map((config) => (
                  <SettingField
                    key={config.key}
                    config={config}
                    value={editing[config.key] || ""}
                    isSet={settings[config.key]?.is_set}
                    onChange={(val) => handleChange(config.key, val)}
                  />
                ))}
            </section>

            <section className="settings-section">
              <h3>Notifications</h3>
              <p className="section-description">
                Configure where Anna sends reminder and schedule notifications.
              </p>
              {settingsConfig
                .filter((c) => c.section === "notifications")
                .map((config) => (
                  <SettingField
                    key={config.key}
                    config={config}
                    value={editing[config.key] || ""}
                    isSet={settings[config.key]?.is_set}
                    onChange={(val) => handleChange(config.key, val)}
                  />
                ))}
            </section>

            <section className="settings-section">
              <h3>Voice &amp; TTS</h3>
              <p className="section-description">
                Configure Anna's text-to-speech voice. F5-TTS runs locally, while Google provides a cloud voice.
              </p>
              {settingsConfig
                .filter((c) => c.section === "voice")
                .map((config) => (
                  <SettingField
                    key={config.key}
                    config={config}
                    value={editing[config.key] || ""}
                    isSet={settings[config.key]?.is_set}
                    onChange={(val) => handleChange(config.key, val)}
                  />
                ))}
            </section>

            <section className="settings-section">
              <h3>Calendars</h3>
              <p className="section-description">
                Connect external calendars to sync events into Anna's calendar.
                Google Calendar is connected via your Google account. Apple Calendar
                requires an ICS subscription URL.
              </p>
              <div className="setting-field">
                <div className="setting-info">
                  <label>Google Calendar meetings</label>
                  <p>
                    {user?.googleCalendarWriteEnabled
                      ? "Connected. Anna can create events and send invitations."
                      : "Reconnect Google to let Anna create events and send invitations."}
                  </p>
                </div>
                <button type="button" className="btn-secondary" onClick={login}>
                  {user?.googleCalendarWriteEnabled ? "Reconnect Google" : "Enable meeting booking"}
                </button>
              </div>
              {settingsConfig
                .filter((c) => c.section === "calendars")
                .map((config) => (
                  <SettingField
                    key={config.key}
                    config={config}
                    value={editing[config.key] || ""}
                    isSet={settings[config.key]?.is_set}
                    onChange={(val) => handleChange(config.key, val)}
                  />
                ))}
            </section>

            <section className="settings-section">
              <h3>Spotify</h3>
              <p className="section-description">
                Enter your Spotify Developer App credentials, then connect your account.
                Create an app at developer.spotify.com → Dashboard.
              </p>
              {settingsConfig
                .filter((c) => c.section === "spotify")
                .map((config) => (
                  <SettingField
                    key={config.key}
                    config={config}
                    value={editing[config.key] || ""}
                    isSet={settings[config.key]?.is_set}
                    onChange={(val) => handleChange(config.key, val)}
                  />
                ))}
              <div className="spotify-connect-section">
              {spotify.loading ? (
                <div className="settings-loading">Checking Spotify...</div>
              ) : spotify.connected ? (
                <div className="spotify-status">
                  <div className="spotify-connected">
                    <span className="spotify-badge connected">🎵 Connected</span>
                    <span className="spotify-user">{spotify.user}</span>
                    {spotify.product && (
                      <span className="spotify-product">{spotify.product}</span>
                    )}
                  </div>
                  <button
                    className="btn btn-danger-outline"
                    onClick={disconnectSpotify}
                  >
                    Disconnect
                  </button>
                </div>
              ) : (
                <div className="spotify-status">
                  <span className="spotify-badge disconnected">Not connected</span>
                  <button
                    className="btn btn-spotify"
                    onClick={connectSpotify}
                    disabled={spotifyConnecting}
                  >
                    {spotifyConnecting ? "Connecting..." : "🎵 Connect Spotify"}
                  </button>
                </div>
              )}
              </div>
            </section>

            <section className="settings-section">
              <h3>Smart Speaker</h3>
              <p className="section-description">
                Configure the Anna Smart Speaker (Pi 5 + ReSpeaker). Changes take
                effect within 60 seconds.
              </p>
              {settingsConfig
                .filter((c) => c.section === "speaker")
                .map((config) => (
                  <SettingField
                    key={config.key}
                    config={config}
                    value={editing[config.key] || ""}
                    isSet={settings[config.key]?.is_set}
                    onChange={(val) => handleChange(config.key, val)}
                  />
                ))}
            </section>
          </div>

          <div className="settings-actions">
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Settings"}
            </button>
          </div>
        </>
      )}

      {activeTab === "users" && (
        <div className="settings-sections">
          <UsersPanel
            users={users}
            loading={usersLoading}
            currentEmail={user?.email}
            onAction={handleUserAction}
          />
        </div>
      )}
    </div>
  );
}

function SettingField({ config, value, isSet, onChange }) {
  const [showValue, setShowValue] = useState(false);
  const isSecret = !config.type; // fields with no type are secrets
  const isSelect = config.type === "select";

  return (
    <div className="setting-field">
      <div className="setting-field-header">
        <label>{config.label}</label>
        {!isSelect && (
          <span className={`setting-status ${isSet ? "set" : "unset"}`}>
            {isSet ? "Configured" : "Not set"}
          </span>
        )}
      </div>
      <p className="setting-description">{config.description}</p>
      <div className="setting-input-wrapper">
        {isSelect ? (
          <select
            value={value || config.options[0]?.value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="setting-input setting-select"
          >
            {config.options.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ) : (
          <>
            <input
              type={isSecret ? (showValue ? "text" : "password") : (config.type || "text")}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={config.placeholder}
              className="setting-input"
            />
            {isSecret && (
              <button
                className="btn-toggle-visibility"
                onClick={() => setShowValue(!showValue)}
                type="button"
              >
                {showValue ? "Hide" : "Show"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function UsersPanel({ users, loading, currentEmail, onAction }) {
  if (loading) {
    return <div className="settings-loading">Loading users...</div>;
  }

  const approved = users.filter((u) => u.status === "approved");
  const pending = users.filter((u) => u.status === "pending");
  const blocked = users.filter((u) => u.status === "blocked");

  return (
    <>
      {pending.length > 0 && (
        <section className="settings-section">
          <h3>⏳ Pending Approval ({pending.length})</h3>
          <p className="section-description">
            These users are waiting for your approval to access Anna.
          </p>
          <div className="users-list">
            {pending.map((u) => (
              <div key={u.email} className="user-card pending">
                <div className="user-info">
                  {u.picture && (
                    <img src={u.picture} alt="" className="user-avatar" referrerPolicy="no-referrer" />
                  )}
                  <div>
                    <div className="user-name">{u.name || "Unknown"}</div>
                    <div className="user-email">{u.email}</div>
                    <div className="user-date">Requested: {new Date(u.createdAt).toLocaleDateString()}</div>
                  </div>
                </div>
                <div className="user-actions">
                  <button
                    className="btn btn-approve"
                    onClick={() => onAction(u.email, "approve")}
                  >
                    Approve
                  </button>
                  <button
                    className="btn btn-reject"
                    onClick={() => onAction(u.email, "reject")}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="settings-section">
        <h3>✅ Approved Users ({approved.length})</h3>
        <p className="section-description">
          Users with access to Anna.
        </p>
        <div className="users-list">
          {approved.map((u) => (
            <div key={u.email} className="user-card approved">
              <div className="user-info">
                {u.picture && (
                  <img src={u.picture} alt="" className="user-avatar" referrerPolicy="no-referrer" />
                )}
                <div>
                  <div className="user-name">
                    {u.name || "Unknown"}
                    {u.role === "admin" && <span className="user-badge admin">Admin</span>}
                  </div>
                  <div className="user-email">{u.email}</div>
                </div>
              </div>
              {u.email !== currentEmail && (
                <div className="user-actions">
                  <button
                    className="btn btn-block"
                    onClick={() => onAction(u.email, "block")}
                  >
                    Block
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {blocked.length > 0 && (
        <section className="settings-section">
          <h3>🚫 Blocked ({blocked.length})</h3>
          <div className="users-list">
            {blocked.map((u) => (
              <div key={u.email} className="user-card blocked">
                <div className="user-info">
                  {u.picture && (
                    <img src={u.picture} alt="" className="user-avatar" referrerPolicy="no-referrer" />
                  )}
                  <div>
                    <div className="user-name">{u.name || "Unknown"}</div>
                    <div className="user-email">{u.email}</div>
                  </div>
                </div>
                <div className="user-actions">
                  <button
                    className="btn btn-approve"
                    onClick={() => onAction(u.email, "approve")}
                  >
                    Unblock
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

export default Settings;
